import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Breadcrumb from '../components/Breadcrumb.js';
import FishService from '../services/fishService.js'; // Import FishService
import { fishDataFromCsv as localFishData } from '../data/fishDataFromCsv.js';

const FishDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [fish, setFish] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allFish, setAllFish] = useState([]); // To store all fish for compatibility/similar suggestions

  useEffect(() => {
    const fetchFishDetails = async () => {
      try {
        setLoading(true);
        let fishData = await FishService.getFishById(id);
        // Ensure the image_url is a full public URL, explicitly
        if (fishData && fishData.image_url) {
          fishData = { ...fishData, image_url: FishService.getPublicImageUrl(fishData.image_url) };
        } else if (fishData) {
          // Fallback to local data's image_url if API doesn't have it
          const localFish = localFishData.find(f => f.id.toString() === id.toString());
          if (localFish && localFish.image_url) {
            fishData = { ...fishData, image_url: FishService.getPublicImageUrl(localFish.image_url) };
          }
        }
        setFish(fishData);
        console.log('FishDetail: Fetched fish data:', fishData); // Debugging
        console.log('FishDetail: Image URL for main fish:', fishData?.image_url); // Debugging
        
        // Fetch all fish for compatibility/similar suggestions
        const allFishData = await FishService.getAllFish();
        setAllFish(allFishData);
        console.log('FishDetail: All fish data for compatibility:', allFishData); // Debugging

        setLoading(false);
      } catch (err) {
        console.warn('Falling back to local data for FishDetail. API error:', err?.message || err);
        try {
          let localFish = localFishData.find(f => f.id.toString() === id.toString());
          if (localFish && localFish.image_url) {
            localFish = { ...localFish, image_url: FishService.getPublicImageUrl(localFish.image_url) };
          }
          setFish(localFish || null);
          setAllFish(localFishData);
          setError(null);
        } catch (_) {}
        setLoading(false);
      }
    };

    fetchFishDetails();
  }, [id]); // Re-fetch when ID changes

  if (loading) {
    return (
      <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="container">
          <div className="reef-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🐠</div>
            <h1 className="font-display" style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--foreground)', 
              marginBottom: '1rem' 
            }}>
              Loading Fish Details...
            </h1>
            <p style={{ color: 'var(--muted-foreground)', fontSize: '1rem' }}>
              Please wait while we load information about your selected marine fish.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!fish) {
    return (
      <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
        <div className="container">
          <div className="reef-card" style={{ textAlign: 'center', padding: '4rem 2rem' }}>
            <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>🐠</div>
            <h1 className="font-display" style={{ 
              fontSize: '2rem', 
              fontWeight: '600', 
              color: 'var(--foreground)', 
              marginBottom: '1rem' 
            }}>
              Fish Not Found
            </h1>
            <p style={{ color: 'var(--muted-foreground)', fontSize: '1rem', marginBottom: '2rem' }}>
              Sorry, we couldn't find the fish you're looking for in our collection.
            </p>
            <Link to="/shop">
              <button className="btn-hero">
                ← Back to Fish Collection
              </button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const getCareColorClass = (level) => {
    switch(level) {
      case 'beginner': return 'care-beginner';
      case 'intermediate': return 'care-intermediate';
      case 'advanced': return 'care-advanced';
      case 'expert': return 'care-expert';
      default: return 'care-beginner';
    }
  };

  const getCompatibilityFish = () => {
    if (!fish || allFish.length === 0) return [];
    return allFish.filter(f => 
      f.id !== fish.id && 
      f.temperament === fish.temperament && 
      f.reef_safe === fish.reef_safe &&
      f.is_available && f.stock_quantity > 0
    ).slice(0, 3);
  };

  const getSimilarFish = () => {
    if (!fish || allFish.length === 0) return [];
    return allFish.filter(f => 
      f.id !== fish.id && 
      f.care_level === fish.care_level &&
      f.is_available && f.stock_quantity > 0
    ).slice(0, 3);
  };

  return (
    <div style={{ background: 'var(--background)', minHeight: '100vh', paddingTop: '80px' }}>
      <div className="container" style={{ paddingBottom: '80px' }}>
        {/* Breadcrumb Navigation */}
        <Breadcrumb items={[
          { label: 'Home', link: '/' },
          { label: 'Fish Collection', link: '/shop' },
          { label: fish.name }
        ]} />

        {/* Navigation */}
        <div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <button 
            onClick={() => navigate(-1)} 
            className="btn-ghost"
            style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            ← Back
          </button>
          <Link to="/shop">
            <button className="btn-ghost">
              Browse All Fish
            </button>
          </Link>
        </div>

        {/* Main Fish Details */}
        <div className="reef-card" style={{ marginBottom: '3rem', padding: '2rem', overflow: 'hidden' }}>
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', 
            gap: '3rem', 
            alignItems: 'start' 
          }}>
            
            {/* Fish Image */}
            <div style={{ position: 'relative' }}>
              <div style={{
                aspectRatio: '4/3',
                borderRadius: 'var(--radius)',
                overflow: 'hidden',
                background: 'var(--muted)',
                position: 'relative',
                marginBottom: '1.5rem'
              }}>
                {fish.image_url && fish.image_url.trim() !== '' && fish.image_url !== 'null' ? (
                  <img 
                    src={fish.image_url} 
                    alt={fish.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={(e) => {
                      // If image fails to load, hide it and show emoji fallback
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                {(!fish.image_url || fish.image_url.trim() === '' || fish.image_url === 'null') && (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'var(--gradient-ocean)',
                    fontSize: '6rem'
                  }}>
                    {fish.image || '🐠'}
                  </div>
                )}
                
                {/* Badges */}
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.5rem'
                }}>
                  <span style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    background: fish.care_level === 'beginner' ? 'hsl(142, 76%, 36%)' :
                               fish.care_level === 'intermediate' ? 'hsl(45, 93%, 47%)' :
                               fish.care_level === 'advanced' ? 'hsl(24, 74%, 58%)' : 'hsl(348, 83%, 47%)',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {fish.care_level || 'BEGINNER'}
                  </span>
                  {fish.reef_safe && (
                    <span style={{
                      padding: '0.5rem 1rem',
                      borderRadius: '20px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      background: 'hsl(142, 76%, 36%)',
                      color: 'white',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      REEF SAFE
                    </span>
                  )}
                </div>

                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  left: '1rem'
                }}>
                  <span style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '20px',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    background: fish.stock_quantity > 0 ? 'hsl(142, 76%, 36%)' : 'hsl(200, 15%, 45%)',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    {fish.stock_quantity > 0 ? `${fish.stock_quantity} IN STOCK` : 'OUT OF STOCK'}
                  </span>
                </div>
              </div>
              
              {/* Quick Actions */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button 
                  className="btn-hero"
                  disabled={fish.stock_quantity === 0}
                  style={{
                    width: '100%',
                    opacity: fish.stock_quantity === 0 ? 0.5 : 1,
                    cursor: fish.stock_quantity === 0 ? 'not-allowed' : 'pointer'
                  }}
                >
                  {fish.stock_quantity > 0 ? `💬 Add to Inquiry` : '❌ Out of Stock'}
                </button>
                <button className="btn-ghost" style={{ width: '100%' }}>
                  ❤️ Add to Wishlist
                </button>
                <button 
                  className="btn-ghost" 
                  style={{ width: '100%' }}
                  onClick={() => navigate('/contact')}
                >
                  📧 Ask Expert
                </button>
              </div>
            </div>

            {/* Fish Information */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
              <h1 style={{ color: '#2d3436', marginBottom: '10px', fontSize: '2.5rem' }}>
                {fish.name}
              </h1>
              <p style={{ 
                fontStyle: 'italic', 
                color: '#636e72', 
                fontSize: '1.2rem', 
                marginBottom: '20px' 
              }}>
                {fish.scientific_name}
              </p>

              <div style={{ marginBottom: '25px' }}>
                <span className={`care-level ${getCareColorClass(fish.care_level || fish.careLevel)}`} style={{ marginRight: '10px' }}>
                  {(fish.care_level || fish.careLevel || 'beginner').toUpperCase()} CARE
                </span>
                {fish.reef_safe && (
                  <span className="care-level" style={{ background: '#00b894', color: 'white' }}>
                    REEF SAFE ✓
                  </span>
                )}
              </div>


              <p style={{ 
                fontSize: '1.1rem', 
                lineHeight: '1.8', 
                marginBottom: '30px',
                color: '#2d3436' 
              }}>
                {fish.description}
              </p>

              {/* Specifications Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '20px',
                background: '#f8f9fa',
                padding: '20px',
                borderRadius: '10px'
              }}>
                <div>
                  <h4 style={{ color: 'var(--ocean-deep)', marginBottom: '8px', fontWeight: 'bold' }}>📏 Maximum Size</h4>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{fish.size}</p>
                </div>
                <div>
                  <h4 style={{ color: 'var(--ocean-deep)', marginBottom: '8px', fontWeight: 'bold' }}>🍽️ Diet</h4>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{fish.diet}</p>
                </div>
                <div>
                  <h4 style={{ color: 'var(--ocean-deep)', marginBottom: '8px', fontWeight: 'bold' }}>😊 Temperament</h4>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{fish.temperament}</p>
                </div>
                <div>
                  <h4 style={{ color: 'var(--ocean-deep)', marginBottom: '8px', fontWeight: 'bold' }}>🌍 Origin</h4>
                  <p style={{ margin: 0, fontWeight: 'bold' }}>{fish.origin}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Care Requirements */}
        <div className="card" style={{ marginBottom: '40px' }}>
          <h2 style={{ color: '#2d3436', marginBottom: '30px' }}>🐠 Care Requirements</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
            <div>
              <h3 style={{ color: 'var(--ocean-deep)', marginBottom: '15px', fontWeight: 'bold' }}>🌡️ Water Parameters</h3>
              <ul style={{ lineHeight: '2', color: '#2d3436' }}>
                <li><strong>Temperature:</strong> 72-78°F (22-26°C)</li>
                <li><strong>pH:</strong> 8.1-8.4</li>
                <li><strong>Salinity:</strong> 1.020-1.025 SG</li>
                <li><strong>Ammonia/Nitrite:</strong> 0 ppm</li>
                <li><strong>Nitrate:</strong> &lt;20 ppm</li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: 'var(--ocean-mid)', marginBottom: '15px', fontWeight: 'bold' }}>🏠 Tank Requirements</h3>
              <ul style={{ lineHeight: '2', color: '#2d3436' }}>
                <li><strong>Minimum Tank Size:</strong> {fish.care_level === 'beginner' ? '30' : fish.care_level === 'intermediate' ? '50' : '75'} gallons</li>
                <li><strong>Live Rock:</strong> Recommended</li>
                <li><strong>Hiding Places:</strong> Essential</li>
                <li><strong>Water Flow:</strong> Moderate</li>
                <li><strong>Lighting:</strong> {fish.reef_safe ? 'Reef lighting suitable' : 'Standard marine lighting'}</li>
              </ul>
            </div>
            <div>
              <h3 style={{ color: 'var(--ocean-light)', marginBottom: '15px', fontWeight: 'bold' }}>🍽️ Feeding Guide</h3>
              <ul style={{ lineHeight: '2', color: '#2d3436' }}>
                <li><strong>Frequency:</strong> 2-3 times daily</li>
                <li><strong>Diet Type:</strong> {fish.diet}</li>
                <li><strong>Food Size:</strong> Small, bite-sized pieces</li>
                <li><strong>Variety:</strong> {fish.diet === 'Carnivore' ? 'Frozen mysis, brine shrimp' : fish.diet === 'Herbivore' ? 'Marine algae, vegetables' : 'Mixed marine foods'}</li>
                <li><strong>Supplements:</strong> High-quality marine vitamins</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Compatibility Section */}
        {getCompatibilityFish().length > 0 && (
          <div className="card" style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#2d3436', marginBottom: '30px' }}>🤝 Compatible Tank Mates</h2>
            <div className="grid">
              {getCompatibilityFish().map(compatibleFish => (
                <div key={compatibleFish.id} className="fish-card" style={{ transform: 'scale(0.9)' }}>
                  <div className="fish-image" style={{ height: '150px', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--muted)', position: 'relative' }}>
                    {compatibleFish.image_url && compatibleFish.image_url.trim() !== '' && compatibleFish.image_url !== 'null' ? (
                      <img 
                        src={compatibleFish.image_url} 
                        alt={compatibleFish.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--gradient-ocean)',
                        fontSize: '40px'
                      }}>
                        {compatibleFish.image || '🐠'}
                      </div>
                    )}
                  </div>
                  <div className="fish-info">
                    <h4>{compatibleFish.name}</h4>
                    <p style={{ fontSize: '14px' }}>{compatibleFish.description.substring(0, 100)}...</p>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
                      <Link to={`/fish/${compatibleFish.id}`} className="btn" style={{ padding: '5px 10px', fontSize: '12px' }}>
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Similar Fish Section */}
        {getSimilarFish().length > 0 && (
          <div className="card" style={{ marginBottom: '40px' }}>
            <h2 style={{ color: '#2d3436', marginBottom: '30px' }}>🌊 Similar Care Level Fish</h2>
            <div className="grid">
              {getSimilarFish().map(similarFish => (
                <div key={similarFish.id} className="fish-card" style={{ transform: 'scale(0.9)' }}>
                  <div className="fish-image" style={{ height: '150px', borderRadius: 'var(--radius)', overflow: 'hidden', background: 'var(--muted)', position: 'relative' }}>
                    {similarFish.image_url && similarFish.image_url.trim() !== '' && similarFish.image_url !== 'null' ? (
                      <img 
                        src={similarFish.image_url} 
                        alt={similarFish.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                          e.target.nextSibling.style.display = 'flex';
                        }}
                      />
                    ) : (
                      <div style={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'var(--gradient-ocean)',
                        fontSize: '40px'
                      }}>
                        {similarFish.image || '🐠'}
                      </div>
                    )}
                  </div>
                  <div className="fish-info">
                    <h4>{similarFish.name}</h4>
                    <p style={{ fontSize: '14px' }}>{similarFish.description.substring(0, 100)}...</p>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: '10px' }}>
                      <Link to={`/fish/${similarFish.id}`} className="btn" style={{ padding: '5px 10px', fontSize: '12px' }}>
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Shipping Information */}
        <div className="card" style={{ marginBottom: '40px' }}>
          <h2 style={{ color: '#2d3436', marginBottom: '30px' }}>🚚 Shipping & Guarantee</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '30px' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '15px' }}>📦</div>
              <h3 style={{ color: '#00cec9', marginBottom: '10px', fontWeight: 'bold' }}>Safe Packaging</h3>
              <p>Professional insulated boxes with oxygen packs for stress-free transport.</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '15px' }}>⚡</div>
              <h3 style={{ color: '#55efc4', marginBottom: '10px', fontWeight: 'bold' }}>Express from Maldives</h3>
              <p>Overnight delivery to India, Sri Lanka, Dubai, and nearby countries. Your fish arrives within 24 hours.</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '15px' }}>💎</div>
              <h3 style={{ color: '#fdcb6e', marginBottom: '10px', fontWeight: 'bold' }}>30-Day Guarantee</h3>
              <p>Full health guarantee with replacement or refund if not satisfied.</p>
            </div>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '40px', marginBottom: '15px' }}>🧑‍⚕️</div>
              <h3 style={{ color: '#e84393', marginBottom: '10px', fontWeight: 'bold' }}>Expert Support</h3>
              <p>Marine biology experts available for care guidance and support.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FishDetail;
